import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Link from '@/components/shared/Link';
import Collapse from '@/components/shared/Collapse';
import DefaultLayout from '@/components/layout/DefaultLayout';
import { CommonPageProps, ImageShape, NonUndefined, Product, ProductState, VideoShape } from '@/types';
import { getCommonPageProps } from '@/api/common';
import { tp } from '@/typograf';
import { getProduct } from '@/api/product';
import { toCurrency } from '@/utils/to-currency';
import { useRouter } from 'next/router';
import CollapseArrowSVG from '@/svg/collapse-arrow.svg';
import { CSSProperties, Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useMediaQueryDeviceState } from '@/atoms/media-query-device';
import { CATEGORIES_IDS, CategoryId, CATEGORIES_TEXT_DICT, CATEGORIES_SLUGS } from '@/api/categories';
import { useSetSelectedServiceValueFormState } from '@/atoms/form/selected-service-value-form-state';
import { FEEDBACK_POPUP_NAME } from '@/components/general/FeedbackPopup/FeedbackPopup';
import { useSetSelectedItemsState } from '@/atoms/form/selected-items-state';
import { useRoubleToDollarCourseState } from '@/atoms/rouble-to-dollar-course';
import PlaySVG from '@/svg/play.svg';
import proofPreviewImage from '../../../../public/img/proof-preview.jpg';
import dynamic from 'next/dynamic';
import { getSiteSettings } from '@/api/site-settings';
import { useSetOpenedPopupsState } from '@/atoms/opened-popups';
import Button from '@/components/shared/Button';
import Image from 'next/image';
import ShareButton from '@/components/shared/ShareButton';
import { useCookies } from 'react-cookie';
import { RECENTS_COOKIES_NAME } from '@/pages/viewed';
import { addUniqueItemToArray } from '@/utils/arrays/add-unique-item-to-array';
import { YD_NEW_ID } from '@/metrics/ym';
import VideoPopup, { VIDEO_POPUP_NAME } from '@/components/general/VideoPopup/VideoPopup';
import { useTypograf } from '@/hooks/use-typograf';
import { stateDict } from '@/dicts';
import { useSetFeedbackPopupDataNameState } from '@/components/general/FeedbackPopup/use-feedback-popup-data-name-atom';
import { useMediaQuery } from 'usehooks-ts';
import DownloadVideoButton from '@/components/shared/DownloadVideoButton';
import { m, AnimatePresence } from 'framer-motion';
import { getRecProducts } from '@/api/rec-products';

const FavoriteButton = dynamic(() => import('@/components/shared/Card/FavoriteButton'), { ssr: false });
const CompareButton = dynamic(() => import('@/components/shared/Card/CompareButton'), { ssr: false });
const Card = dynamic(() => import('@/components/shared/Card'));
const ProductSlider = dynamic(() => import('@/components/shared/ProductSlider'), { ssr: false });

type collapsesKeys = 'characteristic' | 'brand' | 'description';

const collapsesMap: Record<collapsesKeys, number> = {
    characteristic: 0,
    brand: 1,
    description: 2,
};

const recommendationsTitleIndex = 0;

type CollapsedSectionProps = {
    title: string;
    link?: ReactNode;
    collapsed: boolean;
    children: ReactNode;
};

const CollapsedSectionWrapper = ({ title, collapsed, children }: CollapsedSectionProps) => {
    const [mediaQueryDevice] = useMediaQueryDeviceState();
    const tpTitle = useTypograf(title);

    if (mediaQueryDevice !== 'desktop') {
        return (
            <Collapse collapsed={collapsed}>
                <Collapse.Toggler className="product-collapse-section-title h2">{tpTitle}</Collapse.Toggler>
                <Collapse.Content>
                    <div className="product-collapse-section__inner">{children}</div>
                </Collapse.Content>
            </Collapse>
        );
    }
    return children;
};

const DetailPage = ({
    h1,
    price,
    productState,
    reference,
    diameter,
    isStandardForm,
    accessories,
    color,
    type,
    params,
    insert,
    collection,
    mechanism,
    bracelet,
    powerReserve,
    dialColor,
    materials,
    brand,
    genders,
    options,
    productImages,
    productVideo,
    productVideos,
    additionalCharacteristics,
    accordions,
    recommendations,
    productId,
    categoryId,
    available,
    ytLink,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const router = useRouter();
    const setServiceValueFormState = useSetSelectedServiceValueFormState();
    const setFeedbackPopupDataName = useSetFeedbackPopupDataNameState();

    const { openPopup } = useSetOpenedPopupsState();
    const setSelectedItems = useSetSelectedItemsState();
    const [course] = useRoubleToDollarCourseState();
    const currentCategoryCookiesName = `${RECENTS_COOKIES_NAME}-${categoryId}`;
    const [cookies, setCookie] = useCookies(
        Object.keys(CATEGORIES_SLUGS).map((item) => `${RECENTS_COOKIES_NAME}-${item}`),
    );
    const viewed = cookies[RECENTS_COOKIES_NAME]?.includes(productId);

    const containerRef = useRef<HTMLDivElement>(null);
    const [mediaQueryDevice] = useMediaQueryDeviceState();

    const isMobile = useMediaQuery('(max-width: 767px), (max-width: 900px) and (orientation: landscape)');
    const [activeCollapseIndex, setActiveCollapseIndex] = useState<number | null>(null);
    const [isAnyExpanded, setIsAnyExpanded] = useState(true);
    const [activeVideo, setActiveVideo] = useState<VideoShape | null>(null);

    const onToggleHandler = (key: collapsesKeys) => {
        if (activeCollapseIndex === collapsesMap[key]) {
            setIsAnyExpanded(false);
        } else {
            isAnyExpanded ? setIsAnyExpanded(true) : setTimeout(() => setIsAnyExpanded(true), 500);
            setActiveCollapseIndex(collapsesMap[key]);
        }
    };

    const tpH1 = useTypograf(h1);
    const tpBrandName = useTypograf(brand.name ?? '');
    const tpBrandDescription = useTypograf(brand.description ?? '');
    const tpReference = useTypograf(reference ?? '');
    const tpAccessories = useTypograf(accessories ?? '');
    const tpColor = useTypograf(color ?? '');
    const tpType = useTypograf(type ?? '');
    const tpParams = useTypograf(params ?? '');
    const tpInsert = useTypograf(insert ?? '');
    const tpCollection = useTypograf(collection ?? '');
    const tpMechanism = useTypograf(mechanism ?? '');
    const tpBracelet = useTypograf(bracelet ?? '');
    const tpPowerReserve = useTypograf(powerReserve ?? '');
    const tpDialColor = useTypograf(dialColor ?? '');
    const tpOriginalityText = useTypograf(
        'Каждое изделие проходит проверку. Мы подготовили для вас видео с процессом проверки товара.',
    );

    const productRightRef = useRef<HTMLDivElement>(null);
    const [productRighWidth, setProductRighWidth] = useState(productRightRef.current?.offsetWidth);

    useEffect(() => {
        const onResize = () => {
            isMobile && setProductRighWidth(productRightRef.current?.offsetWidth);
        };

        setTimeout(() => {
            onResize();
        }, 0);

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [isMobile]);

    useEffect(() => {
        if (!viewed) {
            if (cookies[currentCategoryCookiesName]) {
                setCookie(
                    currentCategoryCookiesName,
                    addUniqueItemToArray(cookies[currentCategoryCookiesName], Number(productId)),
                );
            } else {
                setCookie(currentCategoryCookiesName, [Number(productId)]);
            }
        }
    }, [viewed, productId, setCookie, currentCategoryCookiesName]);

    return (
        <DefaultLayout ref={containerRef}>
            <Button
                icon={<Image src="/img/back.svg" alt="" unoptimized fill />}
                iconReverse
                onClick={() => {
                    window.history.length > 1
                        ? router.back()
                        : router.push(`/catalog?category=${categoryId}&sort=published_at&order=desc`);
                }}
                className="product-back-button-touch back-button-clickable"
                geometryVariant="arrow"
            >
                Назад
            </Button>
            <div className="product-layout">
                <div className="product-center">
                    <div className="product-buttons">
                        <FavoriteButton variant="product" productId={Number(productId)} className="product-button" />
                        <ShareButton
                            className="product-button btn-share"
                            url={typeof window !== 'undefined' ? window.location.href : ''}
                            title={h1}
                        />
                        <CompareButton
                            variant="card"
                            productId={Number(productId)}
                            categoryId={categoryId}
                            className="product-button"
                        />
                        <AnimatePresence>
                            {activeVideo && (
                                <m.div
                                    className="download-video-button"
                                    initial={{ opacity: 0 }}
                                    animate={{
                                        opacity: 1,
                                        transition: {
                                            duration: 0.5,
                                        },
                                    }}
                                    exit={{
                                        opacity: 0,
                                    }}
                                >
                                    <DownloadVideoButton activeVideo={activeVideo} />
                                </m.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <ProductSlider
                        images={productImages}
                        video={productVideo}
                        videos={productVideos}
                        onDownloadVideo={setActiveVideo}
                    />
                </div>

                <div className="product-columns">
                    <div className="product-left">
                        {mediaQueryDevice !== 'desktop' && (
                            <div className="product-left__available">
                                <div
                                    className="product-available-text text-xs"
                                    dangerouslySetInnerHTML={{
                                        __html: available ? 'в&nbsp;наличии в&nbsp;Москве' : 'под заказ',
                                    }}
                                />
                                <Button
                                    tag={Link}
                                    href={`/catalog?category=${categoryId}&brands[]=${brand.id}&sort=published_at&order=desc`}
                                    icon={<Image src="/img/back-reverse.svg" alt="" unoptimized fill />}
                                    className="product-all-available product-all-available--top button-arrow-reverse"
                                    geometryVariant="arrow"
                                >
                                    Все {CATEGORIES_TEXT_DICT[categoryId]}
                                    {` ${brand.name}`}
                                </Button>
                                {!available && (
                                    <span className="product-available-subtext text-xs">
                                        {tp('Стоимость может отличаться. Уточняйте у менеджера')}
                                    </span>
                                )}
                            </div>
                        )}
                        <CollapsedSectionWrapper collapsed={false} title="О товаре">
                            <div className="product-left__top">
                                <Button
                                    icon={<Image src="/img/back.svg" alt="" unoptimized fill />}
                                    iconReverse
                                    onClick={() => {
                                        window.history.length > 1
                                            ? router.back()
                                            : router.push(`/catalog?category=${categoryId}`);
                                    }}
                                    className="product-back-button-desktop back-button-clickable"
                                    geometryVariant="arrow"
                                >
                                    Назад
                                </Button>
                                <Button
                                    tag={Link}
                                    href={`/catalog?category=${categoryId}&brands[]=${brand.id}&sort=published_at&order=desc`}
                                    icon={<Image src="/img/back-reverse.svg" alt="" unoptimized fill />}
                                    className="product-all-available button-arrow-reverse"
                                    geometryVariant="arrow"
                                >
                                    Все {CATEGORIES_TEXT_DICT[categoryId]}
                                    {` ${brand.name}`}
                                </Button>
                            </div>

                            <div className="product-left__center">
                                {mediaQueryDevice === 'desktop' && (
                                    <div className="product-left__available">
                                        <div
                                            className="product-available-text text-xs"
                                            dangerouslySetInnerHTML={{
                                                __html: available ? 'в&nbsp;наличии в&nbsp;Москве' : 'под заказ',
                                            }}
                                        />
                                        {!available && (
                                            <span className="product-available-subtext text-xs">
                                                {tp('Стоимость может отличаться. Уточняйте у менеджера')}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div className="product-name-wrapper">
                                    <Button
                                        geometryVariant="mustache"
                                        disabled
                                        size="sm"
                                        tag="div"
                                        visuallyDisabled={true}
                                        className="product-state"
                                    >
                                        {stateDict[productState]}
                                    </Button>
                                    <h1 className="h2 product-name">{tpH1}</h1>
                                </div>

                                {price !== 0 && (
                                    <div className="product-info">
                                        <div className="product-price text-price">
                                            <div className="product-price--dollar">{toCurrency(price, 'USD')}</div>
                                            {course && course !== 0 && (
                                                <>
                                                    <div className="price--delimeter">/&nbsp;</div>
                                                    <div className="price--rouble">
                                                        {toCurrency(price * course, 'RUB')}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {!available && price === 0 && (
                                    <Button
                                        className="product-zero-price"
                                        geometryVariant="mustache"
                                        onClick={() => {
                                            setServiceValueFormState('под заказ');
                                            setFeedbackPopupDataName('order');
                                            openPopup(FEEDBACK_POPUP_NAME);
                                            window.ym?.(YD_NEW_ID, 'reachGoal', 'click_on_request');
                                        }}
                                    >
                                        <span className="text-price">под заказ</span>
                                    </Button>
                                )}

                                {price !== 0 && (
                                    <div className="product-lower-price">
                                        <Button
                                            geometryVariant="mustache"
                                            className="product-lower-price-button"
                                            onClick={() => {
                                                setServiceValueFormState('Нашли дешевле?');
                                                setSelectedItems([h1]);
                                                setFeedbackPopupDataName('cheaper');
                                                openPopup(FEEDBACK_POPUP_NAME);
                                            }}
                                        >
                                            Нашли дешевле?
                                        </Button>

                                        <div className="product-lower-price__text text-s">
                                            Нашли дешевле? Нажми, чтобы получить лучшее ценовое предложение
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="product-cta">
                                <Button
                                    variant="bordered-light"
                                    className="product-trade-in-button"
                                    onClick={() => {
                                        setServiceValueFormState('Trade-in');
                                        setSelectedItems([h1]);
                                        openPopup(FEEDBACK_POPUP_NAME);
                                        setFeedbackPopupDataName('trade-in');
                                        window.ym?.(YD_NEW_ID, 'reachGoal', 'click_trade_in_button');
                                    }}
                                >
                                    Trade-in
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        setServiceValueFormState('Купить');
                                        setSelectedItems([h1]);
                                        setFeedbackPopupDataName('empty');
                                        openPopup(FEEDBACK_POPUP_NAME);
                                        window.ym?.(YD_NEW_ID, 'reachGoal', 'click_on_the_buy_button');
                                    }}
                                >
                                    Купить
                                </Button>
                            </div>

                            <div className="product-proof">
                                <button
                                    className="product-proof__image"
                                    onClick={() => {
                                        openPopup(VIDEO_POPUP_NAME);
                                    }}
                                >
                                    <Image
                                        src={proofPreviewImage.src}
                                        width={proofPreviewImage.width}
                                        height={proofPreviewImage.height}
                                        alt=""
                                        className="product-proof__image-el"
                                    />
                                    <div className="product-proof__image-icon">
                                        <PlaySVG />
                                    </div>
                                </button>

                                <div className="product-proof__right">
                                    <div className="product-proof__title text-xs">100% оригинал</div>
                                    <div
                                        className="product-proof__description text-s"
                                        dangerouslySetInnerHTML={{ __html: tpOriginalityText }}
                                    />
                                </div>
                            </div>

                            <div className="product-text-block">
                                <div className="m-text-xs product-text-block__title">Гарантия</div>
                                <div
                                    className="text-s product-text-block__text"
                                    dangerouslySetInnerHTML={{
                                        __html: `Гарантия на&nbsp;подержанные часы и&nbsp;украшения не&nbsp;менее 12&nbsp;месяцев.
                                    На&nbsp;новые изделия распространяется международная гарантия не&nbsp;менее
                                    2&nbsp;лет.
                                    <br />
                                    <br />
                                    Все изделия проходят очную экспертизу подлинности в&nbsp;часовой мастерской.
                                    <br />
                                    <br />
                                    Кроме того, мы&nbsp;предоставляем 1&nbsp;год гарантии на&nbsp;случай заводского
                                    брака.
                                    <br />
                                    <br />
                                    Будьте уверены, что если товар окажется неисправным, не&nbsp;будет соответствовать описанию <nobr>и т. п.</nobr>,<br />
                                    RotorMine либо заменит товар, либо отремонтирует, либо предоставит полный возврат средств, включая стоимость расходов.`,
                                    }}
                                ></div>
                            </div>
                        </CollapsedSectionWrapper>
                    </div>

                    <div
                        ref={productRightRef}
                        className="product-right"
                        style={
                            {
                                '--product-right-width': `${productRighWidth}px`,
                                '--collapse-delay': `${isAnyExpanded ? 0.5 : 0}s`,
                            } as CSSProperties
                        }
                    >
                        <CollapsedSectionWrapper title="Подробнее" collapsed={true}>
                            <Collapse
                                className="product-collapse"
                                collapsed={
                                    !activeCollapseIndex || activeCollapseIndex === collapsesMap['characteristic']
                                        ? false
                                        : true
                                }
                            >
                                <Collapse.Toggler
                                    className="product-collapse-toggler"
                                    onToggle={() => onToggleHandler('characteristic')}
                                >
                                    <div className="product-collapse-options text-xs">характеристики</div>
                                    <div className="product-collapse-toggler__icon">
                                        <CollapseArrowSVG />
                                    </div>
                                </Collapse.Toggler>
                                <Collapse.Content>
                                    <div className="product-collapse-content">
                                        <ul className="product-characteristic list-unstyled">
                                            {brand.name !== '' && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        название бренда
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        <Button
                                                            tag={Link}
                                                            icon={<Image src="/img/back.svg" alt="" unoptimized fill />}
                                                            href={`/catalog?category=${categoryId}&brands[]=${brand.id}&sort=published_at&order=desc`}
                                                            className="button-arrow-reverse"
                                                            geometryVariant="arrow"
                                                        >
                                                            {tpBrandName}
                                                        </Button>
                                                    </div>
                                                </li>
                                            )}
                                            {reference && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Ref
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {tpReference}
                                                    </div>
                                                </li>
                                            )}
                                            {diameter && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        диаметр
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {isStandardForm ? `${diameter} мм` : diameter}
                                                    </div>
                                                </li>
                                            )}
                                            {accessories && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Цвет Фурнитуры
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {tpAccessories}
                                                    </div>
                                                </li>
                                            )}
                                            {color && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Цвет
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {tpColor}
                                                    </div>
                                                </li>
                                            )}
                                            {type && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Тип
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {tpType}
                                                    </div>
                                                </li>
                                            )}
                                            {params && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Параметры
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {tpParams}
                                                    </div>
                                                </li>
                                            )}
                                            {insert && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Вставка
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {tpInsert}
                                                    </div>
                                                </li>
                                            )}
                                            {collection && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Коллекция
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {tpCollection}
                                                    </div>
                                                </li>
                                            )}
                                            {mechanism && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Механизм
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {tpMechanism}
                                                    </div>
                                                </li>
                                            )}
                                            {bracelet && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Браслет
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {tpBracelet}
                                                    </div>
                                                </li>
                                            )}
                                            {powerReserve && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Запас хода
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {tpPowerReserve}
                                                    </div>
                                                </li>
                                            )}
                                            {dialColor && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Цвет циферблата
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {tpDialColor}
                                                    </div>
                                                </li>
                                            )}
                                            {materials && materials.length > 0 && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Материал
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {materials.map((item) => item.name).join(', ')}
                                                    </div>
                                                </li>
                                            )}
                                            {genders && genders.length > 0 && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Гендеры
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {genders.map((item) => item.name).join(', ')}
                                                    </div>
                                                </li>
                                            )}
                                            {options && options.length > 0 && (
                                                <li className="product-characteristic-item">
                                                    <div className="product-characteristic-item__name m-text-xs">
                                                        Опции
                                                    </div>
                                                    <div className="product-characteristic-item__value m-text-xs">
                                                        {options.map((item) => item.name).join(', ')}
                                                    </div>
                                                </li>
                                            )}
                                            {additionalCharacteristics &&
                                                additionalCharacteristics.map((item, i) => {
                                                    return (
                                                        <li className="product-characteristic-item" key={i}>
                                                            <div className="product-characteristic-item__name m-text-xs">
                                                                {item.name}
                                                            </div>
                                                            <div className="product-characteristic-item__value m-text-xs">
                                                                {item.value}
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                        </ul>
                                    </div>
                                </Collapse.Content>
                            </Collapse>
                            {tpBrandDescription && (
                                <Collapse
                                    className="product-collapse"
                                    collapsed={activeCollapseIndex === collapsesMap['brand'] ? false : true}
                                >
                                    <Collapse.Toggler
                                        className="product-collapse-toggler"
                                        onToggle={() => onToggleHandler('brand')}
                                    >
                                        <div className="text-xs">О бренде</div>
                                        <div className="product-collapse-toggler__icon">
                                            <CollapseArrowSVG />
                                        </div>
                                    </Collapse.Toggler>
                                    <Collapse.Content>
                                        <div className="product-collapse-content">
                                            <div
                                                className="product-collapse-text text-s"
                                                dangerouslySetInnerHTML={{ __html: tpBrandDescription }}
                                            />
                                        </div>
                                    </Collapse.Content>
                                </Collapse>
                            )}
                            {accordions.map((item, i) => {
                                return (
                                    item.text && (
                                        <Collapse
                                            className="product-collapse"
                                            collapsed={
                                                activeCollapseIndex === collapsesMap['description'] ? false : true
                                            }
                                            key={i}
                                        >
                                            <Collapse.Toggler
                                                className="product-collapse-toggler"
                                                onToggle={() => onToggleHandler('description')}
                                            >
                                                <div className="text-xs">{item.name}</div>
                                                <div className="product-collapse-toggler__icon">
                                                    <CollapseArrowSVG />
                                                </div>
                                            </Collapse.Toggler>
                                            <Collapse.Content>
                                                <div className="product-collapse-content">
                                                    <div
                                                        className="product-collapse-text text-s"
                                                        dangerouslySetInnerHTML={{ __html: item.text }}
                                                    />
                                                </div>
                                            </Collapse.Content>
                                        </Collapse>
                                    )
                                );
                            })}
                        </CollapsedSectionWrapper>
                    </div>
                </div>
            </div>
            {recommendations.length > 0 && (
                <div className="wrapper">
                    <ul className="list-unstyled cards-list product-cards-list">
                        {recommendations
                            .sort((a, b) => {
                                if (a.price === 0 && b.price !== 0) {
                                    return 1;
                                } else if (a.price !== 0 && b.price === 0) {
                                    return -1;
                                } else {
                                    return 0;
                                }
                            })
                            .map((item, i) => {
                                return (
                                    <Fragment key={item.id}>
                                        {i === recommendationsTitleIndex && (
                                            <li className="cards-list__item card-list-title">
                                                <div className="card-list-title__text h1">Вам может быть интересно</div>
                                            </li>
                                        )}
                                        <li
                                            className={classNames('cards-list__item', {
                                                'cards-list__item--wide': i === 3,
                                            })}
                                        >
                                            <Card
                                                productId={item.id}
                                                slug={item.slug}
                                                cardName={item.name}
                                                price={item.price}
                                                materialName={item.material_name}
                                                state={item.state}
                                                images={item.images}
                                                reference={item.reference}
                                                diameter={item.diameter}
                                                isStandardForm={item.is_standard_form}
                                                size={item.size}
                                                accessories={item.accessories}
                                                productColor={item.color}
                                                type={item.type}
                                                params={item.params}
                                                insert={item.insert}
                                                width={i === 3 ? 'wide' : 'default'}
                                                categoryId={item.category_id}
                                                available={item.available}
                                            />
                                        </li>
                                    </Fragment>
                                );
                            })}
                    </ul>
                </div>
            )}
            <VideoPopup />
        </DefaultLayout>
    );
};

export default DetailPage;

type ProductPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        categoryId: CategoryId;
        price: number;
        available: boolean;
        productState: ProductState;
        reference: string | null;
        diameter: string | null;
        isStandardForm: boolean;
        accessories: string | null;
        color: string | null;
        type: string | null;
        params: string | null;
        insert: string | null;
        collection: string | null;
        mechanism: string | null;
        bracelet: string | null;
        powerReserve: string | null;
        dialColor: string | null;
        materials: {
            id: number;
            slug: string;
            name: string;
        }[];
        brand: {
            id: number;
            slug: string;
            name: string;
            description: string;
        };
        genders: {
            id: number;
            slug: string;
            name: string;
        }[];
        options: {
            id: number;
            name: string;
        }[];
        productImages: ImageShape[];
        productVideo: VideoShape;
        productVideos: VideoShape[];
        additionalCharacteristics: {
            name: string;
            value: string;
        }[];
        accordions: {
            name: string;
            text: string;
        }[];
        recommendations: Omit<Product, 'brand_name' | 'favorite'>[];
        productId: string;
        ytLink: string;
    }
>;

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async ({ query }) => {
    const productId = query.id as string;
    if (!productId) {
        return { notFound: true };
    }

    const productInfo = await getProduct(productId)
        .then((res) => res)
        .catch((err) => err);

    if (productInfo && productInfo.status === 'error') {
        if (productInfo.status === 404) {
            return { notFound: true };
        }
        throw productInfo.repsonse.data.errors[0];
    }

    const [commonPageProps, recomendations, staticText] = await Promise.all([
        getCommonPageProps(),
        getRecProducts({
            params: { product_id: Number(productId), limit: 5 },
        }),
        getSiteSettings({
            keys: ['youtube_link'],
        }),
    ]);

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'product-page',
            meta: {
                ...commonPageProps.meta,
                ...productInfo?.data.meta,
            },
            h1: tp(productInfo?.data.name || ''),
            price: productInfo?.data?.price || 0,
            productState: productInfo?.data?.state,
            reference: productInfo?.data?.reference || null,
            diameter: productInfo?.data?.diameter || null,
            isStandardForm: productInfo?.data?.is_standard_form ?? true,
            accessories: productInfo?.data?.accessories || null,
            color: productInfo?.data?.color || null,
            type: productInfo?.data?.type || null,
            params: productInfo?.data?.params || null,
            insert: productInfo?.data?.insert || null,
            collection: productInfo?.data?.collection || null,
            mechanism: productInfo?.data?.mechanism || null,
            bracelet: productInfo?.data?.bracelet || null,
            powerReserve: productInfo?.data?.power_reserve || null,
            dialColor: productInfo?.data?.dial_color || null,
            available: productInfo?.data?.available,
            materials:
                productInfo?.data?.materials.map((item: { id: number; slug: string; name: string }) => {
                    return {
                        ...item,
                        name: tp(item.name),
                    };
                }) || [],
            brand: productInfo?.data?.brand || {
                id: 0,
                slug: '',
                name: '',
                description: '',
            },
            genders:
                productInfo?.data?.genders.map((item: { id: number; slug: string; name: string }) => {
                    return {
                        ...item,
                        name: tp(item.name),
                    };
                }) || [],
            options:
                productInfo?.data?.options.map((item: { id: number; name: string }) => {
                    return {
                        ...item,
                        name: tp(item.name),
                    };
                }) || [],
            productImages: productInfo?.data?.images || [],
            productVideo: productInfo?.data.video ?? [],
            productVideos: productInfo?.data.videos ?? [],
            additionalCharacteristics:
                productInfo?.data?.additional_characteristics.map((item: { name: string; value: string }) => {
                    return {
                        name: tp(item.name),
                        value: tp(item.value),
                    };
                }) || [],
            accordions: (productInfo?.data?.accordions ?? [])
                .map((item: { name: string; text: string }) => {
                    return {
                        name: tp(item.name),
                        text: typeof item.text === 'string' ? tp(item.text) : item.text,
                    };
                })
                .filter((item: any) => !!item.text),
            recommendations: (recomendations.data?.items || []).map((item) => ({
                id: item.id,
                slug: item.slug,
                name: item.name,
                price: item.price,
                material_name: item.material ?? '',
                state: item.state,
                images: item.images,
                reference: item.reference,
                diameter: (item.diameter ?? '').toString(),
                size: item.size ?? '',
                accessories: item.accessories ?? '',
                productColor: item.color ?? '',
                type: item.type ?? '',
                insert: item.insert ?? '',
                category_id: productInfo?.data?.category_id as CategoryId,
                available: item.available,
            })),
            productId,
            categoryId: productInfo?.data?.category_id || CATEGORIES_IDS['watch'],
            ytLink: staticText.data?.youtube_link ?? '',
        } satisfies ProductPageProps,
    };
};
